/**
 * KAN-135 — what to drop when a TPA (messaging provider) is re-authorized
 * with a DIFFERENT provider account.
 *
 * Two tiers:
 *
 * 1. Account-bound token extras (the real fix). `tokens/tpa` collects fields
 *    the backend and modules derive from the connected provider account —
 *    Twilio's Messaging Service SID and the voice API key minted from the OLD
 *    account SID, RingCentral's webhook subscription, Pinnacle/WhatsApp WABA
 *    numbers. Every credential save merges, so after switching accounts the
 *    backend mixes the NEW sid with the OLD api key (voice tokens fail) and
 *    forces the OLD `service_sid` onto sends. These are deleted in the SAME
 *    write as the new credentials, so nothing stale survives the save. The
 *    modules re-derive them afterwards — but only if their one-time
 *    localStorage sentinels are cleared first (see clearTPAReauthSentinels).
 *
 * 2. Per-user history the old account produced (optional tier, backend):
 *    notifications, the three activity-log lists, workflow error logs and
 *    their unread counters. Wiped via the TPA_REAUTH_CLEANUP service action at
 *    the explicit re-auth completion points only — never on first-time auth.
 *    Org-wide history is wiped only when the license admin re-authorizes;
 *    that wipe also covers every org member's notifications (resolved from
 *    `uaOrgUsers` on the backend — the wipe flag lives only in the admin's
 *    browser). A shared member's own re-auth wipes just their notifications.
 *    Only items created BEFORE the account change are deleted, so a late
 *    retry never takes the new account's notifications or logs with it;
 *    unread counters are capped at what remains instead of reset.
 *
 * Both tiers apply ONLY when the provider ACCOUNT changed (Twilio: a new
 * `sid`; Calendly: a new `organization`; RingCentral: a new account id read
 * from /account/~ — never the owner/person, since a colleague re-authorizing
 * the same account keeps history; Intercom: a new workspace). The credential
 * save / OAuth callback that
 * detects the change stamps `tpaAccountChangedAt` (SERVER time) on the
 * extension doc, atomically with the credentials; the backend wipe honours
 * it whenever it runs — late retries included — and clears it afterwards;
 * a same-account save clears it too. No expiry: every delete is bounded to
 * items older than the stamp, so a late wipe only ever removes the previous
 * account's history.
 * Re-entering the same account (or rotating the auth token / API key) keeps
 * everything. Providers whose credentials carry no account id are treated as
 * the same account — a rotated key must never wipe history.
 *
 * Org-shared docs (`serviceProcessedEvents`, `entityMapsCreatedInServiceForSearch`,
 * `rtDBNotificationIDVsDataMap`) and chat history are deliberately untouched.
 *
 * 3. Detaching the OLD account (Twilio). Wiping history is not enough when
 *    the old account keeps pushing NEW events: the sink receiver fans out to
 *    every org in TWILIO_ACCOUNT_SINK_MAPS/{oldSid}, and the old Messaging
 *    Service webhook still targets this org. BaseTPAModule.saveCredentials()
 *    calls `disconnectPreviousAccount()` BEFORE writing the new credentials
 *    (backend actions read the stored ones); TwilioModule trims this org from
 *    the old sink map (shared-safe: Twilio resources go only when no org is
 *    left) and blanks the old service's webhook URLs. Best effort — never
 *    blocks the save.
 */
import { deleteField } from 'firebase/firestore';
import { callFunction } from '@/lib/firebase/functions';
import { API_ENDPOINTS } from '@/config/constants';

/** Fields in `tokens/tpa` (and the `{saas}_orgs` copy) that belong to the previous provider account. */
export const TPA_ACCOUNT_BOUND_TOKEN_FIELDS = [
  'service_sid',            // Twilio Messaging Service created for the old account
  'twilio_apikey',          // voice API key minted from the old account SID
  'twilio_apisecret',
  'twilio_apikey_createdTime',
  'twilio_apikey_sid',      // which account SID the voice key was minted for
  'subscription_id',        // RingCentral webhook subscription
  'wa_numbers',             // Pinnacle WABA numbers
  'wabaId',                 // WhatsApp embedded-signup WABA
  'phoneNumberId',
] as const;

/** Spread into a merge `setDoc` payload to delete the account-bound extras in the same write. */
export function accountBoundFieldDeletes(): Record<string, ReturnType<typeof deleteField>> {
  const out: Record<string, ReturnType<typeof deleteField>> = {};
  for (const f of TPA_ACCOUNT_BOUND_TOKEN_FIELDS) out[f] = deleteField();
  return out;
}

/**
 * Per-TPA credential field that identifies the provider ACCOUNT — never the
 * secret, which the user may rotate. Keyed by module id. A TPA that is not
 * listed cannot tell accounts apart, so its saves count as the same account.
 */
export const TPA_ACCOUNT_IDENTITY_FIELDS: Record<string, string> = {
  twilio: 'sid',
  twiliocall: 'sid',
  'acuity-scheduling': 'userid',
  bulksms: 'token_id',
  clicksend: 'username',
  exotel: 'sid',
  gupshup: 'appId',
  kaleyra: 'SID',
  plivo: 'auth_id',
  rivet: 'ClientId',
  routemobile: 'username',
  routemobilewa: 'username',
  sinch: 'service_plan_id',
  telebu: 'authId',
  textmagic: 'username',
};

/**
 * One-time localStorage sentinels that stop the Twilio / RingCentral modules
 * from re-saving `service_sid` / `subscription_id` once they have done so.
 * After an account change those fields were just deleted, so the sentinels
 * must go too or the modules never write the new account's values back.
 */
export function clearTPAReauthSentinels(extensionName: string | null | undefined): void {
  if (!extensionName) return;
  try {
    localStorage.removeItem(`ua_twl_service_id_set_${extensionName}`);
    localStorage.removeItem(`ua_rc_sub_id_set_${extensionName}`);
  } catch { /* unavailable */ }
}

/**
 * Local caches that describe the provider ACCOUNT rather than the app: the
 * 24h voice-number list and the softphone token / TwiML app id. Left alone
 * they keep offering the old account's caller IDs and register the softphone
 * against the old account for up to an hour.
 */
export function clearTPAAccountCaches(extensionName: string | null | undefined): void {
  if (!extensionName) return;
  try {
    localStorage.removeItem('ulgebra_voice_numbers_cache');
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && (k.startsWith(`ua_voice_token_${extensionName}_`) || k.startsWith(`ua_voice_twiml_app_${extensionName}_`))) {
        doomed.push(k);
      }
    }
    doomed.forEach((k) => localStorage.removeItem(k));
  } catch { /* unavailable */ }
}

/**
 * localStorage key holding the extension whose per-user history must be
 * wiped. localStorage (not sessionStorage) so a closed tab does not lose it.
 * Set only once a re-auth is KNOWN to have succeeded with a changed account
 * (credential save saw a new account id; OAuth popup landed on
 * authorization-success). Removed once the backend confirms, or after the
 * retry cap. Sign-out clears it.
 */
export const TPA_REAUTH_WIPE_KEY = 'ua_tpa_reauth_wipe';
const TPA_REAUTH_WIPE_TRIES_KEY = 'ua_tpa_reauth_wipe_tries';
const TPA_REAUTH_WIPE_MAX_TRIES = 3;

/**
 * sessionStorage stamp set together with the pending re-auth flag. Every
 * window opened during the re-auth (popup, "open in new tab", the
 * popup-blocked "Continue" button) inherits a copy, so the
 * authorization-success page can tell a TPA re-auth from a SaaS one.
 */
export const TPA_OAUTH_POPUP_KEY = 'ua_tpa_oauth_popup';

/** localStorage key other open tabs listen to, so they reload onto the new account too. */
export const TPA_REAUTH_BROADCAST_KEY = 'ua_tpa_reauth_broadcast';

export function markTPAReauthWipe(extensionName: string | null | undefined): void {
  if (!extensionName) return;
  try { localStorage.setItem(TPA_REAUTH_WIPE_KEY, extensionName); } catch { /* unavailable */ }
}

/** True while an old-account history wipe for this extension is still owed. Screens mask old data meanwhile. */
export function isTPAReauthWipePending(extensionName: string | null | undefined): boolean {
  if (!extensionName) return false;
  try { return localStorage.getItem(TPA_REAUTH_WIPE_KEY) === extensionName; } catch { return false; }
}

export function clearTPAReauthWipe(): void {
  try {
    localStorage.removeItem(TPA_REAUTH_WIPE_KEY);
    localStorage.removeItem(TPA_REAUTH_WIPE_TRIES_KEY);
  } catch { /* unavailable */ }
}

/** Tell other tabs of this app that the provider account changed; they reload. */
export function broadcastTPAReauth(extensionName: string | null | undefined): void {
  if (!extensionName) return;
  try { localStorage.setItem(TPA_REAUTH_BROADCAST_KEY, `${extensionName}:${Date.now()}`); } catch { /* */ }
}

let broadcastListening = false;
/** Reload this tab when another tab of the same app completes an account change. Idempotent. */
export function listenForTPAReauthInOtherTabs(getExtensionName: () => string | null | undefined): void {
  if (broadcastListening || typeof window === 'undefined') return;
  broadcastListening = true;
  window.addEventListener('storage', (e) => {
    if (e.key !== TPA_REAUTH_BROADCAST_KEY || !e.newValue) return;
    const ext = getExtensionName();
    if (ext && e.newValue.startsWith(`${ext}:`)) {
      console.log('[TPA re-auth] account changed in another tab — reloading');
      window.location.reload();
    }
  });
}

let wipeInFlight: Promise<void> | null = null;

/**
 * Run the backend history wipe if one is flagged. Best-effort; resolves either
 * way. Single-flight per tab, and gives up (with a toast) after a few failed
 * attempts so a permanent backend error cannot re-fire the deletes on every
 * boot. A 'partial' answer (time budget hit) keeps the flag without counting
 * as a failure: the deletes it issued are committed, so retries converge.
 */
export function runPendingTPAReauthWipe(): Promise<void> {
  if (wipeInFlight) return wipeInFlight;
  wipeInFlight = runWipe().finally(() => { wipeInFlight = null; });
  return wipeInFlight;
}

async function runWipe(): Promise<void> {
  let extensionName: string | null = null;
  let tries = 0;
  try {
    extensionName = localStorage.getItem(TPA_REAUTH_WIPE_KEY);
    tries = Number(localStorage.getItem(TPA_REAUTH_WIPE_TRIES_KEY) || '0');
  } catch { return; }
  if (!extensionName) return;
  if (tries >= TPA_REAUTH_WIPE_MAX_TRIES) {
    console.warn('[TPA re-auth] history wipe gave up after', tries, 'attempts');
    clearTPAReauthWipe();
    try {
      const { toast } = await import('sonner');
      toast.warning('Could not finish clearing the previous account’s data. Some old notifications or logs may remain.');
    } catch { /* toast unavailable */ }
    return;
  }
  // A flagged wipe means the account changed — let the modules re-derive the
  // account-bound extras the credential write deleted, and drop the local
  // caches that still describe the old account.
  clearTPAReauthSentinels(extensionName);
  clearTPAAccountCaches(extensionName);
  let failed = false;
  try {
    // History lives under the effective (admin) uid; notifications under the
    // caller's own. Pass both identities the way the other service actions do.
    const { useAuthStore } = await import('@/stores/auth-store');
    const { impersonatedUserId, selectedAdminUserId } = useAuthStore.getState();
    const res = await callFunction<
      Record<string, unknown>,
      { status?: string; userResponse?: boolean; skipped?: boolean; failed?: string[] }
    >(API_ENDPOINTS.SERVICE_API, {
      appCode: extensionName,
      action: 'TPA_REAUTH_CLEANUP',
      ...(impersonatedUserId ? { _ua_lic_impersonateUserID: impersonatedUserId } : {}),
      ...(selectedAdminUserId ? { _ua_lic_adminUserID: selectedAdminUserId } : {}),
    });
    if (res?.status === 'success') {
      clearTPAReauthWipe();
      console.log(res?.skipped
        ? '[TPA re-auth] same account — history kept for'
        : '[TPA re-auth] old-account history wiped for', extensionName);
      // The badge was masked to 0 while the flag was set; the RTDB listener
      // only re-fires on a change, so re-read the real count once.
      try {
        const { useNotificationStore } = await import('@/stores/notification-store');
        await useNotificationStore.getState().refreshUnreadCount();
      } catch { /* best-effort */ }
      return;
    }
    if (res?.status === 'partial') {
      console.log('[TPA re-auth] history wipe still in progress — will continue at next boot');
      return;
    }
    failed = true;
    console.warn('[TPA re-auth] history wipe incomplete — will retry at next boot', res);
  } catch (err) {
    failed = true;
    console.warn('[TPA re-auth] history wipe failed — will retry at next boot', err);
  }
  if (failed) {
    try { localStorage.setItem(TPA_REAUTH_WIPE_TRIES_KEY, String(tries + 1)); } catch { /* */ }
  }
}
